import type { NodeGroup } from '../../types/project';

interface Props {
  x: number;
  y: number;
  selectedCount: number;
  readOnly?: boolean;
  canPaste?: boolean;
  focusedGroupId?: string | null;
  focusedGroupName?: string | null;
  anchorInGroup?: boolean;
  anchorGroupName?: string | null;
  selectionInGroup?: boolean;
  existingGroups?: NodeGroup[];
  onCenter: () => void;
  onEdit: () => void;
  onAddRoute?: () => void;
  onCopy: () => void;
  onCut: () => void;
  onPaste: () => void;
  onDelete: () => void;
  onGroupSelection?: () => void;
  onAddToGroup?: (groupId: string) => void;
  onRemoveFromGroup?: () => void;
  onEnterGroup?: () => void;
  onExitGroup?: () => void;
  onClose: () => void;
}

function MenuItem({
  label,
  onClick,
  danger = false,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`block w-full px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-40 ${
        danger
          ? 'text-danger hover:bg-surface-overlay'
          : 'text-text hover:bg-surface-overlay'
      }`}
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
    >
      {label}
    </button>
  );
}

function MenuDivider() {
  return <div className="my-1 border-t border-border" role="separator" />;
}

function MenuHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-text-subtle">
      {children}
    </div>
  );
}

export default function NodeContextMenu({
  x,
  y,
  selectedCount,
  readOnly = false,
  canPaste = false,
  focusedGroupId = null,
  focusedGroupName = null,
  anchorInGroup = false,
  anchorGroupName = null,
  selectionInGroup = false,
  existingGroups = [],
  onCenter,
  onEdit,
  onAddRoute,
  onCopy,
  onCut,
  onPaste,
  onDelete,
  onGroupSelection,
  onAddToGroup,
  onRemoveFromGroup,
  onEnterGroup,
  onExitGroup,
  onClose,
}: Props) {
  const run = (action: () => void) => {
    action();
    onClose();
  };

  const selectionLabel =
    selectedCount === 1 ? '1 node selected' : `${selectedCount} nodes selected`;

  const copyLabel = selectedCount > 1 ? `Copy ${selectedCount} nodes` : 'Copy';
  const cutLabel = selectedCount > 1 ? `Cut ${selectedCount} nodes` : 'Cut';
  const deleteLabel =
    selectedCount > 1 ? `Delete ${selectedCount} nodes` : 'Delete';

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default"
        aria-label="Close menu"
        onClick={onClose}
      />
      <div
        className="fixed z-50 min-w-[200px] max-w-[260px] rounded-lg border border-border bg-surface-raised py-1 shadow-lg"
        style={{ left: x, top: y }}
        role="menu"
        onContextMenu={(e) => e.preventDefault()}
      >
        <MenuHeader>{selectionLabel}</MenuHeader>
        {focusedGroupId && focusedGroupName && (
          <div className="px-3 pb-1 text-[10px] text-sky-300">
            Inside group: {focusedGroupName}
          </div>
        )}

        <MenuItem label="Center on selection" onClick={() => run(onCenter)} />
        <MenuItem
          label={selectedCount > 1 ? 'Edit first node' : 'Edit node'}
          onClick={() => run(onEdit)}
        />
        {onAddRoute && (
          <MenuItem label="Add route" onClick={() => run(onAddRoute)} />
        )}

        <MenuDivider />
        <MenuItem label={copyLabel} onClick={() => run(onCopy)} />
        {!readOnly && (
          <>
            <MenuItem label={cutLabel} onClick={() => run(onCut)} />
            <MenuItem
              label="Paste"
              onClick={() => run(onPaste)}
              disabled={!canPaste}
            />

            <MenuDivider />
            <MenuHeader>Groups</MenuHeader>
            {focusedGroupId && onExitGroup && (
              <MenuItem label="Exit group" onClick={() => run(onExitGroup)} />
            )}
            {!focusedGroupId && anchorInGroup && onEnterGroup && (
              <MenuItem
                label={
                  anchorGroupName
                    ? `Open group “${anchorGroupName}”`
                    : 'Open group'
                }
                onClick={() => run(onEnterGroup)}
              />
            )}
            {selectedCount >= 1 && onGroupSelection && (
              <MenuItem
                label={
                  selectedCount > 1
                    ? `Group ${selectedCount} nodes`
                    : 'Group node'
                }
                onClick={() => run(onGroupSelection)}
              />
            )}
            {selectedCount >= 1 &&
              onAddToGroup &&
              existingGroups.map((group) => (
                <MenuItem
                  key={group.id}
                  label={`Add to “${group.name}”`}
                  onClick={() => run(() => onAddToGroup(group.id))}
                />
              ))}
            {selectedCount >= 1 && onRemoveFromGroup && selectionInGroup && (
              <MenuItem
                label={
                  selectedCount > 1
                    ? 'Remove from group'
                    : 'Remove from group'
                }
                onClick={() => run(onRemoveFromGroup)}
              />
            )}

            <MenuDivider />
            <MenuItem
              label={deleteLabel}
              onClick={() => run(onDelete)}
              danger
            />
          </>
        )}
      </div>
    </>
  );
}
